import * as React from 'react'

import type { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import i18n from 'modules/core/lib/i18n'

import type { BranchOperation } from 'modules/automations/lib/Store/Branch'
import { ConditionTypes } from 'modules/automations/lib/enums'
import { TriggerAttributes } from 'modules/automations/lib/triggerAttributes'
import { Operation } from 'modules/automations/store/models/Operation'
import { ITriggerCondition } from 'modules/automations/store/models/Trigger'
import type CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'

import ValueSelect from './ValueSelect'

interface Props {
  disabled: boolean
  form: Form
  operation: BranchOperation
  label: string
  loadingCustomFields: boolean
  customFieldsCollection: CustomFieldsCollection
  hideHasChanged: boolean
  condition?: ITriggerCondition
  extensionFeatureHasConditions: boolean
}

export const Selects = observer((props: Props) => {
  const {
    disabled,
    form,
    operation,
    label,
    loadingCustomFields,
    customFieldsCollection,
    condition,
    hideHasChanged,
    extensionFeatureHasConditions,
  } = props

  // Determines if the "has_changed" operator should be available
  const shouldRenderHasChanged = !hideHasChanged

  const operatorOptions = React.useMemo(() => {
    const elements = [
      {
        label: i18n.t('automations.operations.equals'),
        value: Operation.Operators.equals,
      },
      {
        label: i18n.t('automations.operations.not_equals'),
        value: Operation.Operators.not_equals,
      },
    ]

    if (shouldRenderHasChanged) {
      elements.push({
        label: i18n.t('automations.operations.has_changed'),
        value: Operation.Operators.has_changed,
      })
    }

    return elements
  }, [shouldRenderHasChanged])

  const newIsTextField =
    condition?.type === ConditionTypes.String &&
    form.get('operator').value !== Operation.Operators.has_changed

  const oldIsTextField = React.useMemo(() => {
    if (
      operation.attribute === TriggerAttributes.custom_fields ||
      operation.attribute === TriggerAttributes.custom_resources
    ) {
      return false
    }

    if (operation.id !== null) return true

    switch (operation.attribute) {
      case TriggerAttributes.first_name:
      case TriggerAttributes.last_name:
      case TriggerAttributes.age:
      case TriggerAttributes.city:
      case TriggerAttributes.state:
      case TriggerAttributes.identifier:
      case TriggerAttributes.bank_number:
      case TriggerAttributes.addressLine1:
      case TriggerAttributes.addressLine2:
      case TriggerAttributes.postalCode:
      case TriggerAttributes.social_security_number:
        return form.get('operator').value !== Operation.Operators.has_changed
      default:
        return false
    }
  }, [operation, form])

  const isTextField = extensionFeatureHasConditions ? newIsTextField : oldIsTextField

  const isNumberField =
    condition?.type === ConditionTypes.Number &&
    form.get('operator').value !== Operation.Operators.has_changed

  const valueIsVisible =
    !isTextField && form.get('operator').value !== Operation.Operators.has_changed

  // Note: original condition always returns true since an attribute can't be both document_public and gender at the same time
  const shouldRenderOperator =
    operation.attribute !== TriggerAttributes.document_public ||
    operation.attribute !== TriggerAttributes.gender

  return (
    <>
      <FormRow>
        {shouldRenderOperator && (
          <Select
            none={i18n.t('common.select_one')}
            readonly={disabled}
            field={form.get('operator')}
            label={label}
            options={operatorOptions}
          />
        )}
      </FormRow>
      <FormRow>
        {valueIsVisible && (
          <ValueSelect
            disabled={disabled}
            form={form}
            operation={operation}
            loadingCustomFields={loadingCustomFields}
            customFieldsCollection={customFieldsCollection}
            condition={condition}
            extensionFeatureHasConditions={extensionFeatureHasConditions}
          />
        )}
        {isTextField && (
          <Text
            label={i18n.t('automations.actions.branch.value')}
            field={form.get('value')}
            readonly={disabled}
          />
        )}
        {isNumberField && (
          <Text
            label={i18n.t('automations.actions.branch.value')}
            field={form.get('value')}
            type="number"
            readonly={disabled}
          />
        )}
      </FormRow>
    </>
  )
})
